import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  ArrowLeft,
  Check,
  MessageCircle,
  Sparkles,
  X,
  ArrowRight,
  Info,
  Upload,
} from "lucide-react";
import { DESIGNS, COLLECTIONS } from "./designs.data";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Fabric library — placeholder swatches until real fabric close-up photos are uploaded.
 * Each fabric has a visual (SVG pattern via CSS gradient) + spec block.
 */
const FABRICS = [
  {
    id: "TCH-180",
    name: "Techno Messy",
    gsm: "170–180 GSM · 36\" Tube",
    tag: "Full Dull",
    blurb:
      "Dense knit with a subtle messy hex-pattern texture. Matte non-reflective finish — perfect for premium sublimation.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/llkxr0zt_WhatsApp%20Image%202026-09-16%20at%2017.07.17%20%281%29.jpeg",
  },
  {
    id: "MDK-180",
    name: "Micro Dot Knit",
    gsm: "180 GSM · 36\" Tube",
    tag: "Pique-Feel",
    blurb:
      "Fine micro-dot polyester knit. Silky drape, wicks sweat, holds sublimation like a print lab.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/vseykuhb_WhatsApp%20Image%202026-09-16%20at%2017.07.18.jpeg",
  },
  {
    id: "CCJ-170",
    name: "Criss Cross Jaquard",
    gsm: "160–170 GSM · 36\" Tube",
    tag: "Jacquard",
    blurb:
      "Woven-look jacquard with a diagonal criss-cross weave. Adds structure and depth without any print required.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/s4d5ktaw_WhatsApp%20Image%202026-09-16%20at%2017.07.18%20%281%29.jpeg",
  },
  {
    id: "AMJ-180",
    name: "Abstract Messy Jaquard",
    gsm: "170–180 GSM · 36\" Tube",
    tag: "Full Dull",
    blurb:
      "Abstract geometric jacquard on a fully matte base. Elevated look for premium fashion sports jerseys.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/yi8oazb7_WhatsApp%20Image%202026-09-16%20at%2017.07.17.jpeg",
  },
  {
    id: "PPS-180",
    name: "Premium Micro PP Softy",
    gsm: "180 GSM · 36\" Tube",
    tag: "Ultra-Soft",
    blurb:
      "Buttery-soft micro-polyester with a smooth face. Draped, satin-adjacent hand — feels closer to fashion than function.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/m927ltem_WhatsApp%20Image%202026-09-16%20at%2017.05.07%20%281%29.jpeg",
  },
  {
    id: "MRK-180",
    name: "Micro Rice Knit",
    gsm: "180 GSM · 36\" Dia Tube",
    tag: "Breathable",
    blurb:
      "Rice-grain textured knit that maximises airflow. Go-to for performance jerseys where breathability rules.",
    image:
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/5hwjnqng_WhatsApp%20Image%202026-09-16%20at%2017.05.07.jpeg",
  },
];

/**
 * Design library — 100 real jersey patterns extracted from Customade design PDFs.
 * Patterns are INDICATIVE — the same layout can be produced as cricket, football
 * or basketball jerseys (or any team sport).
 * Sourced from `./designs.data.js`.
 */

function encodeMessage(fabrics, designs, refUrl) {
  const lines = [
    "Hi Customade!",
    "I picked these from your sportswear catalogue:",
    "",
  ];
  if (fabrics.length) {
    lines.push("FABRICS:");
    fabrics.forEach((f) => lines.push(`• ${f.name} ${f.gsm} (${f.id})`));
    lines.push("");
  }
  if (designs.length) {
    lines.push("DESIGN PATTERNS:");
    designs.forEach((d) => lines.push(`• ${d.id} (Collection ${d.collection})`));
    lines.push("");
  }
  if (refUrl) {
    lines.push("CUSTOM REFERENCE:");
    lines.push(refUrl);
    lines.push("");
  }
  if (designs.length || refUrl) {
    lines.push("Sport: ______  (cricket / football / basketball / other)");
    lines.push("");
  }
  lines.push("Please share pricing, MOQ and delivery timeline.");
  return encodeURIComponent(lines.join("\n"));
}

export default function SportsWear() {
  const [selFab, setSelFab] = useState(() => new Set());
  const [selDes, setSelDes] = useState(() => new Set());
  const [activeCol, setActiveCol] = useState("ALL");
  const [reference, setReference] = useState(null); // { id, url, previewUrl, filename }
  const [refUploading, setRefUploading] = useState(false);
  const [refError, setRefError] = useState("");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow selecting the same file again after clear
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setRefError("Only image files are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setRefError("Image too large. Max 10 MB.");
      return;
    }
    setRefError("");
    setRefUploading(true);
    const previewUrl = URL.createObjectURL(file);
    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch(`${API_BASE}/api/upload-reference`, {
        method: "POST",
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed. Try again.");
      }
      const data = await resp.json();
      const publicUrl = `${API_BASE}${data.url}`;
      setReference({
        id: data.id,
        url: publicUrl,
        previewUrl,
        filename: file.name,
      });
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setRefError(err.message || "Upload failed.");
    } finally {
      setRefUploading(false);
    }
  };

  const clearReference = () => {
    if (reference?.previewUrl) URL.revokeObjectURL(reference.previewUrl);
    setReference(null);
  };

  const fabrics = useMemo(
    () => FABRICS.filter((f) => selFab.has(f.id)),
    [selFab]
  );
  const designs = useMemo(
    () => DESIGNS.filter((d) => selDes.has(d.id)),
    [selDes]
  );
  const visibleDesigns = useMemo(
    () => (activeCol === "ALL" ? DESIGNS : DESIGNS.filter((d) => d.collection === activeCol)),
    [activeCol]
  );
  const total = fabrics.length + designs.length + (reference ? 1 : 0);

  const toggle = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleFab = toggle(setSelFab);
  const toggleDes = toggle(setSelDes);

  const clearAll = () => {
    setSelFab(new Set());
    setSelDes(new Set());
    clearReference();
  };

  const waHref =
    total > 0
      ? `https://wa.me/918169272803?text=${encodeMessage(fabrics, designs, reference?.url)}`
      : "https://wa.me/918169272803?text=" +
        encodeURIComponent(
          "Hi Customade! I'd like to see your sportswear catalogue."
        );

  return (
    <div className="font-body bg-[#FDFDFD] text-[#0A0A0A] min-h-screen">
      {/* Slim nav */}
      <header className="sticky top-0 z-40 bg-white border-b-4 border-black">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 py-4">
          <Link
            to="/"
            data-testid="sw-back-home"
            className="inline-flex items-center gap-2 font-heading uppercase text-sm md:text-base tracking-wider hover:text-[#FF0055] transition-colors"
          >
            <ArrowLeft size={18} strokeWidth={3} />
            <span>Back to Customade</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <span
              className="hidden sm:inline-flex items-center gap-1 nb-border-2 bg-[#FFE600] px-3 py-1.5 font-heading uppercase text-[10px] md:text-xs tracking-widest"
              data-testid="sw-selected-count"
            >
              <Sparkles size={12} strokeWidth={3} /> {total} picked
            </span>
            <a href="tel:8169272803" className="nb-btn nb-btn-magenta" data-testid="sw-call-cta">
              <Phone size={16} strokeWidth={3} />
              <span className="hidden sm:inline">Call Now</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b-4 border-black overflow-hidden" data-testid="sw-hero">
        {/* Decorative geometry */}
        <div className="absolute -top-16 -right-20 w-96 h-96 bg-[#FFE600] nb-border rotate-12 pointer-events-none" />
        <div className="absolute bottom-8 -left-16 w-60 h-60 bg-[#00F0FF] nb-border -rotate-6 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-16 md:py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 nb-border-2 bg-white px-3 py-1.5 mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#FF0055] animate-pulse" />
              <span className="font-heading uppercase text-xs tracking-widest">
                Sportswear catalogue · Live
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="font-heading uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tighter"
              data-testid="sw-hero-title"
            >
              Pick your{" "}
              <span className="bg-[#FFE600] nb-border-2 px-2 inline-block">fabric.</span>
              <br />
              Pick your{" "}
              <span className="text-[#FF0055]">design.</span>
              <br />
              We'll do the rest.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80"
            >
              Tap any fabric swatch and any design mockup below — as many as you
              like. When you're done, hit{" "}
              <span className="font-heading uppercase text-[#0A0A0A] bg-[#00F0FF] nb-border-2 px-1.5">
                Send my selection
              </span>{" "}
              and your picks land in our WhatsApp with real photos + pricing in
              under an hour.
            </motion.p>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end gap-3">
            <div className="nb-border-4 nb-shadow bg-white p-5">
              <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-2">
                How it works
              </div>
              <ol className="text-sm md:text-[15px] space-y-2 leading-relaxed">
                <li className="flex gap-2">
                  <span className="font-heading text-[#FF0055]">01</span>
                  <span>Tap fabrics + designs you like.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-heading text-[#FF0055]">02</span>
                  <span>Or upload your own reference image.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-heading text-[#FF0055]">03</span>
                  <span>Hit "Send my selection" — we reply on WhatsApp.</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Fabrics gallery */}
      <section
        id="fabrics"
        className="border-b-4 border-black py-16 md:py-20 bg-[#F0F0F0]"
        data-testid="sw-fabrics-section"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-12">
            <div>
              <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-3">
                /// Step 01 · Fabrics
              </div>
              <h2 className="font-heading uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                Choose your{" "}
                <span className="underline decoration-[#FF0055] decoration-[8px] underline-offset-4">
                  material.
                </span>
              </h2>
            </div>
            <div className="text-sm text-[#555]">
              {selFab.size} of {FABRICS.length} selected
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {FABRICS.map((f, i) => {
              const isSel = selFab.has(f.id);
              return (
                <motion.button
                  key={f.id}
                  type="button"
                  onClick={() => toggleFab(f.id)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  whileHover={{ y: -3 }}
                  className={`relative text-left nb-border-4 overflow-hidden bg-white transition-shadow ${
                    isSel
                      ? "shadow-[10px_10px_0_0_#FF0055]"
                      : "nb-shadow-sm"
                  }`}
                  aria-pressed={isSel}
                  data-testid={`sw-fabric-${f.id}`}
                >
                  {/* swatch preview */}
                  <div className="relative aspect-[4/3] border-b-4 border-black overflow-hidden bg-[#F0F0F0]">
                    <img
                      src={f.image}
                      alt={`${f.name} fabric close-up`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute top-2 left-2 nb-border-2 bg-white text-[#0A0A0A] px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest">
                      {f.id}
                    </span>
                    <AnimatePresence>
                      {isSel && (
                        <motion.span
                          initial={{ scale: 0, rotate: -20 }}
                          animate={{ scale: 1, rotate: 0 }}
                          exit={{ scale: 0 }}
                          transition={{ type: "spring", stiffness: 400, damping: 18 }}
                          className="absolute top-2 right-2 w-8 h-8 nb-border-2 bg-[#FF0055] text-white flex items-center justify-center"
                        >
                          <Check size={16} strokeWidth={4} />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="p-4">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="font-heading uppercase text-lg leading-tight">
                        {f.name}
                      </h3>
                      <span className="font-heading text-xs text-[#555]">{f.gsm}</span>
                    </div>
                    <div className="mt-1 inline-block nb-border-2 bg-[#FFE600] px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest">
                      {f.tag}
                    </div>
                    <p className="mt-2 text-[13px] leading-snug text-[#0A0A0A]/75">
                      {f.blurb}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Designs gallery */}
      <section
        id="designs"
        className="border-b-4 border-black py-16 md:py-20 bg-white"
        data-testid="sw-designs-section"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6 md:mb-8">
            <div>
              <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-3">
                /// Step 02 · Designs
              </div>
              <h2 className="font-heading uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
                Pick a{" "}
                <span className="bg-[#00F0FF] nb-border-2 px-2 inline-block">
                  pattern.
                </span>
              </h2>
            </div>
            <div className="text-sm text-[#555]">
              {selDes.size} of {DESIGNS.length} selected
            </div>
          </div>

          {/* Indicative disclaimer */}
          <div
            className="nb-border-4 nb-shadow-sm bg-[#FFE600] p-4 md:p-5 mb-8 flex items-start gap-3"
            data-testid="sw-indicative-notice"
          >
            <Info size={22} strokeWidth={3} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading uppercase text-sm md:text-base tracking-tight">
                These patterns are indicative.
              </div>
              <p className="mt-1 text-sm md:text-[15px] leading-snug text-[#0A0A0A]/85">
                Any layout below can be produced as a{" "}
                <span className="font-heading uppercase">Cricket</span>,{" "}
                <span className="font-heading uppercase">Football</span> or{" "}
                <span className="font-heading uppercase">Basketball</span>{" "}
                jersey — same pattern, tailored to the sport's fit and fabric.
                Tell us your sport when you send the selection.
              </p>
            </div>
          </div>

          {/* Collection filter chips */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="sw-collection-filter">
            {COLLECTIONS.map((c) => {
              const isActive = activeCol === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveCol(c.key)}
                  className={`nb-border-2 px-3 py-2 font-heading uppercase text-xs tracking-widest transition-all ${
                    isActive
                      ? "bg-[#0A0A0A] text-[#FFE600] nb-shadow-sm"
                      : "bg-white text-[#0A0A0A] hover:bg-[#F0F0F0]"
                  }`}
                  data-testid={`sw-col-${c.key}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Design tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {visibleDesigns.map((d, i) => {
              const isSel = selDes.has(d.id);
              return (
                <motion.button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDes(d.id)}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.35, delay: (i % 20) * 0.02 }}
                  whileHover={{ y: -2 }}
                  className={`relative text-left nb-border-4 overflow-hidden bg-white aspect-square transition-shadow ${
                    isSel ? "shadow-[8px_8px_0_0_#00F0FF]" : "nb-shadow-sm"
                  }`}
                  aria-pressed={isSel}
                  data-testid={`sw-design-${d.id}`}
                >
                  <img
                    src={d.src}
                    alt={`Jersey design pattern ${d.id} — indicative, can be produced for cricket, football or basketball`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <span className="absolute bottom-2 left-2 nb-border-2 bg-white px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest">
                    {d.id}
                  </span>
                  <AnimatePresence>
                    {isSel && (
                      <motion.span
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 18 }}
                        className="absolute top-2 right-2 w-7 h-7 nb-border-2 bg-[#00F0FF] text-[#0A0A0A] flex items-center justify-center"
                      >
                        <Check size={14} strokeWidth={4} />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-10 text-center text-sm text-[#0A0A0A]/60">
            Have a specific design in mind? Add your picks + describe it in the
            WhatsApp message.
          </div>
        </div>
      </section>

      {/* Step 03 · Custom pattern upload */}
      <section
        id="custom"
        className="border-b-4 border-black py-16 md:py-20 bg-[#F0F0F0]"
        data-testid="sw-custom-section"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="mb-8 md:mb-10">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-3">
              /// Step 03 · Custom pattern
            </div>
            <h2 className="font-heading uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
              Got your own{" "}
              <span className="underline decoration-[#FF0055] decoration-[8px] underline-offset-4">
                reference?
              </span>
            </h2>
            <p className="mt-3 text-sm md:text-base text-[#0A0A0A]/70 max-w-2xl">
              Upload a sketch, screenshot or inspiration image. It gets attached
              as a link in your WhatsApp — we recreate the design in your chosen
              fabric.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Upload dropzone */}
            <div className="nb-border-4 nb-shadow bg-white p-5 md:p-6 flex flex-col">
              <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-3">
                Reference image
              </div>
              {reference ? (
                <div className="relative flex-1 flex flex-col">
                  <div className="relative aspect-video nb-border-2 overflow-hidden bg-[#F0F0F0]">
                    <img
                      src={reference.previewUrl}
                      alt="Your reference"
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute top-2 left-2 nb-border-2 bg-[#FFE600] px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest">
                      Uploaded
                    </div>
                    <button
                      type="button"
                      onClick={clearReference}
                      className="absolute top-2 right-2 nb-border-2 bg-black text-white w-8 h-8 flex items-center justify-center hover:bg-[#FF0055] transition-colors"
                      aria-label="Remove reference"
                      data-testid="sw-ref-remove"
                    >
                      <X size={14} strokeWidth={3} />
                    </button>
                  </div>
                  <div className="mt-3 text-sm break-all">
                    <span className="font-heading uppercase text-[10px] tracking-widest text-[#555]">
                      File:
                    </span>{" "}
                    {reference.filename}
                  </div>
                  <div className="mt-1 text-[12px] text-[#0A0A0A]/60 break-all">
                    Share link → {reference.url}
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="sw-ref-input"
                  className={`flex-1 flex flex-col items-center justify-center text-center nb-border-2 border-dashed p-6 md:p-10 cursor-pointer transition-colors ${
                    refUploading ? "bg-[#FFE600]/40" : "bg-[#FDFDFD] hover:bg-[#FFE600]/25"
                  }`}
                  data-testid="sw-ref-dropzone"
                >
                  <input
                    id="sw-ref-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                    disabled={refUploading}
                    data-testid="sw-ref-input"
                  />
                  <Upload
                    size={40}
                    strokeWidth={2.5}
                    className={refUploading ? "text-[#FF0055] animate-pulse" : "text-[#0A0A0A]"}
                  />
                  <div className="mt-3 font-heading uppercase text-base md:text-lg tracking-tight">
                    {refUploading ? "Uploading…" : "Drop or click to upload"}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-[#0A0A0A]/60">
                    JPG, PNG or WEBP · up to 10 MB
                  </div>
                </label>
              )}
              {refError && (
                <div
                  className="mt-3 nb-border-2 bg-[#FF0055] text-white text-xs md:text-sm px-3 py-2"
                  data-testid="sw-ref-error"
                >
                  {refError}
                </div>
              )}
            </div>

            {/* Helper card */}
            <div className="nb-border-4 nb-shadow bg-[#FFE600] p-5 md:p-6 flex flex-col justify-between">
              <div>
                <div className="font-heading uppercase text-xs tracking-[0.3em] mb-3">
                  What works best
                </div>
                <ul className="space-y-2.5 text-sm md:text-[15px] leading-snug">
                  <li className="flex gap-2">
                    <Check size={16} strokeWidth={3} className="text-[#FF0055] flex-shrink-0 mt-0.5" />
                    <span>A jersey photo from Instagram / Pinterest / a match you loved</span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={16} strokeWidth={3} className="text-[#FF0055] flex-shrink-0 mt-0.5" />
                    <span>A hand-drawn sketch or team artwork (front + back if possible)</span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={16} strokeWidth={3} className="text-[#FF0055] flex-shrink-0 mt-0.5" />
                    <span>Your logo, sponsor marks or team crest</span>
                  </li>
                  <li className="flex gap-2">
                    <Check size={16} strokeWidth={3} className="text-[#FF0055] flex-shrink-0 mt-0.5" />
                    <span>Colour references — hex codes, Pantone or a photo we can pick from</span>
                  </li>
                </ul>
              </div>
              <div className="mt-5 text-xs md:text-[13px] text-[#0A0A0A]/75 leading-snug">
                Your reference gets a private link that only opens the image.
                Send with the WhatsApp message and we take it from there.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky selection tray */}
      <AnimatePresence>
        {total > 0 && (
          <motion.div
            key="tray"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-4 left-4 right-4 md:left-8 md:right-8 z-50"
            data-testid="sw-tray"
          >
            <div className="max-w-[1200px] mx-auto nb-border-4 nb-shadow-lg bg-[#0A0A0A] text-white p-4 md:p-5 flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-5">
              <div className="flex-1 flex flex-wrap items-center gap-2">
                <span className="font-heading uppercase text-xs tracking-[0.3em] text-[#FFE600]">
                  Your picks
                </span>
                <span className="font-heading text-2xl md:text-3xl">{total}</span>
                <div className="flex flex-wrap gap-1.5 ml-2 max-h-16 overflow-hidden">
                  {[...fabrics, ...designs].slice(0, 6).map((it) => (
                    <span
                      key={it.id}
                      className="nb-border-2 bg-white text-[#0A0A0A] px-2 py-0.5 font-heading uppercase text-[10px] tracking-wider"
                    >
                      {it.id}
                    </span>
                  ))}
                  {reference && (
                    <span className="nb-border-2 bg-[#00F0FF] text-[#0A0A0A] px-2 py-0.5 font-heading uppercase text-[10px] tracking-wider">
                      REF
                    </span>
                  )}
                  {fabrics.length + designs.length > 6 && (
                    <span className="nb-border-2 bg-[#FFE600] text-[#0A0A0A] px-2 py-0.5 font-heading uppercase text-[10px] tracking-wider">
                      +{fabrics.length + designs.length - 6} more
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <button
                  onClick={clearAll}
                  className="nb-border-2 bg-transparent text-white px-3 py-2 font-heading uppercase text-xs tracking-wider hover:bg-white hover:text-black transition-colors"
                  data-testid="sw-tray-clear"
                >
                  <X size={14} strokeWidth={3} className="inline mr-1" />
                  Clear
                </button>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nb-btn nb-btn-magenta text-sm md:text-base flex-1 justify-center whitespace-nowrap"
                  data-testid="sw-tray-send"
                >
                  <MessageCircle size={18} strokeWidth={3} />
                  Send my selection
                  <ArrowRight size={16} strokeWidth={3} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fallback footer CTA when nothing selected */}
      {total === 0 && (
        <section className="py-16 bg-[#FFE600] border-b-4 border-black">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 flex flex-wrap items-center justify-between gap-5">
            <div>
              <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#0A0A0A]/70 mb-1">
                Not sure yet?
              </div>
              <div className="font-heading uppercase text-2xl md:text-4xl leading-tight">
                Talk to us. We'll send fabric samples to your door.
              </div>
            </div>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nb-btn nb-btn-black"
              data-testid="sw-generic-cta"
            >
              WhatsApp us <ArrowRight size={18} strokeWidth={3} />
            </a>
          </div>
        </section>
      )}

      {/* Slim footer */}
      <footer className="bg-black text-white/70 py-6 text-xs text-center">
        © {new Date().getFullYear()} Customade · Mumbai · Sportswear Catalogue
      </footer>
    </div>
  );
}
